// トークンマイニングイベント管理システム
import { useEffect } from 'react';

// 生態系別のアイコンとエフェクト設定（森の生態系の橋渡しに基づく）
// 各トークンは生態系の遷移（橋渡し）を表す
const ECOSYSTEM_EFFECTS = {
  QT: { icon: '🌍➡🌱', name: '問い立て' }, // 土壌に種を植える
  DT: { icon: '🌱➡🌳', name: '議論' }, // 種を樹幹に育てる
  TT: { icon: '🌳➡🌸', name: '信任' }, // 樹幹に花を咲かせる
  IT: { icon: '🌸➡🍎', name: '招待' }, // 花に果実を結ぶ
  XT: { icon: '🍎➡🦌', name: '実行' }, // 果実が動植物に活力を与える
  CT: { icon: '🦌➡🫚', name: '支援' }, // 動植物の代謝で根に栄養を与える
  RT: { icon: '🫚➡🍄', name: '振り返り' }, // 根が菌を活性化する
  ET: { icon: '🍄➡🌍', name: '共鳴' }, // 菌が土壌を潤す
  consumption: { icon: '🛒', name: '消費' }, // トークン消費
};

class TokenEventManager {
  constructor() {
    this.listeners = [];
  }

  // マイニングエフェクトのリスナーを追加
  addMiningListener(callback) {
    this.listeners.push(callback);

    // クリーンアップ関数を返す
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }

  // マイニング/消費エフェクトを発火
  triggerMiningEffect(
    tokenSymbol,
    amount,
    source = 'mining',
    isConsumption = false
  ) {
    // ソースから生態系を判定
    const ecosystemType = this.getEcosystemType(source);
    const effect = ECOSYSTEM_EFFECTS[ecosystemType] || ECOSYSTEM_EFFECTS['XT'];

    const eventData = {
      tokenSymbol,
      amount,
      source,
      isConsumption,
      icon: isConsumption ? ECOSYSTEM_EFFECTS.consumption.icon : effect.icon,
      ecosystemName: isConsumption
        ? ECOSYSTEM_EFFECTS.consumption.name
        : effect.name,
      timestamp: Date.now(),
    };

    // すべてのリスナーに通知
    this.listeners.forEach((callback) => {
      try {
        callback(eventData);
      } catch (error) {
        console.error('Token mining effect listener error:', error);
      }
    });
  }

  // ソースから生態系タイプを判定
  getEcosystemType(source) {
    const sourceMap = {
      question: 'QT', // 植物層 - 問い立て
      comment: 'DT', // 昆虫層 - 議論・コメント
      discussion: 'DT', // 昆虫層 - 議論
      resonance: 'ET', // 土壌層 - 共鳴・エンパシー
      empathy: 'ET', // 土壌層 - 共感
      task: 'XT', // 動物層 - タスク実行
      task_complete: 'XT', // 動物層 - 実行完了
      execution: 'XT', // 動物層 - 実行
      review: 'RT', // 菌類層 - 振り返り・評価
      evaluation: 'RT', // 菌類層 - 評価
      contribution: 'CT', // 根系層 - 支援・貢献
      support: 'CT', // 根系層 - 支援
      purchase: 'CT', // 根系層 - 購入支援
      trust: 'TT', // 樹冠層 - 信任
      vote: 'TT', // 樹冠層 - 投票
      invitation: 'IT', // 拡散層 - 招待
      invite: 'IT', // 拡散層 - 招待
      mining: 'XT', // デフォルト
    };
    return sourceMap[source] || 'XT';
  }

  // 特定のアクション後にマイニングをトリガー（コメント投稿、共鳴など）
  triggerActionMining(actionType, amount = null) {
    const miningMap = {
      comment: { symbol: 'SHINKETSU', amount: amount || 5, source: 'comment' },
      resonance: {
        symbol: 'SHINKETSU',
        amount: amount || 3,
        source: 'resonance',
      },
      question: {
        symbol: 'SHINKETSU',
        amount: amount || 10,
        source: 'question',
      },
      task_complete: {
        symbol: 'SHINKETSU',
        amount: amount || 15,
        source: 'task',
      },
      purchase: {
        symbol: 'SHINKETSU',
        amount: amount || 2,
        source: 'purchase',
      },
    };

    const miningData = miningMap[actionType];
    if (miningData) {
      this.triggerMiningEffect(
        miningData.symbol,
        miningData.amount,
        miningData.source
      );
    }
  }
}

// シングルトンインスタンス
export const tokenEventManager = new TokenEventManager();

// トークン消費をトリガー
class TokenConsumptionManager {
  triggerConsumption(amount, purpose = 'purchase') {
    tokenEventManager.triggerMiningEffect(
      'SHINKETSU',
      -Math.abs(amount),
      purpose,
      true
    );
  }
}

export const tokenConsumptionManager = new TokenConsumptionManager();

// 便利な関数群
export const triggerMiningEffect = (
  tokenSymbol,
  amount,
  source,
  isConsumption = false
) => {
  tokenEventManager.triggerMiningEffect(
    tokenSymbol,
    amount,
    source,
    isConsumption
  );
};

export const triggerTokenConsumption = (amount, purpose) => {
  tokenConsumptionManager.triggerConsumption(amount, purpose);
};

export const triggerActionMining = (actionType, amount) => {
  tokenEventManager.triggerActionMining(actionType, amount);
};

// フック用
export const useMiningEvents = (callback) => {
  useEffect(() => {
    const cleanup = tokenEventManager.addMiningListener(callback);
    return cleanup;
  }, [callback]);
};
